import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bell,
  FileText,
  GraduationCap,
  Video,
  Megaphone,
  ClipboardCheck,
  CheckCheck,
  Star,
} from "lucide-react";
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "../services/notifications.service";
import { formatDate } from "./datetime";

const POLL_MS = 30_000;

const KIND_ICON = {
  "assignment.created": FileText,
  "assignment.due_reminder": FileText,
  "assignment.submitted": ClipboardCheck,
  "result.published": GraduationCap,
  "exam.published": GraduationCap,
  "exam.rescheduled": GraduationCap,
  "exam.cancelled": GraduationCap,
  "meeting.created": Video,
  "meeting.reminder": Video,
  "announcement.created": Megaphone,
  "material.low_rating": Star,
  system: Bell,
};

function relativeTime(iso, t) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return t("notifications.just_now");
  const m = Math.round(s / 60);
  if (m < 60) return t("notifications.minutes_ago", { count: m });
  const h = Math.round(m / 60);
  if (h < 24) return t("notifications.hours_ago", { count: h });
  const d = Math.round(h / 24);
  if (d < 7) return t("notifications.days_ago", { count: d });
  return formatDate(iso);
}

/**
 * NotificationBell — topbar bell with unread badge (polls every 30s) + a
 * dropdown of recent notifications. Shared by student & teacher shells.
 */
export default function NotificationBell({ role = "student" }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      const r = await getUnreadCount();
      setUnread(r?.unread || 0);
    } catch {
      /* silent */
    }
  }, []);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      const r = await getNotifications({ status: "all", pageSize: 8 });
      setItems(r?.items || []);
      setUnread(r?.unread || 0);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll the cheap unread-count endpoint + refresh on focus and on a custom
  // event other components fire after they read/clear notifications.
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    window.addEventListener("focus", refreshCount);
    window.addEventListener("lms-notif-refresh", refreshCount);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", refreshCount);
      window.removeEventListener("lms-notif-refresh", refreshCount);
    };
  }, [refreshCount]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const handleClick = async (n) => {
    setOpen(false);
    if (!n.readAt) {
      try {
        await markNotificationRead(n._id);
        setUnread((u) => Math.max(0, u - 1));
      } catch {
        /* silent */
      }
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
    } catch {
      /* silent */
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        className="ux-ring relative h-10 w-10 rounded-xl ux-text-soft hover:ux-text hover:ux-surface-2 flex items-center justify-center transition"
        title={t("notifications.title")}
        aria-label={t("notifications.title")}
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-[var(--ux-surface)]">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[min(360px,calc(100vw_-_1.5rem))] max-h-[480px] flex flex-col ux-surface border ux-border rounded-2xl ux-shadow-lg overflow-hidden z-[2000]">
          <div className="px-4 py-3 border-b ux-border flex items-center justify-between">
            <span className="text-[13px] font-semibold ux-text">
              {t("notifications.title")} {unread > 0 && <span className="ux-text-muted">· {t("notifications.new_count", { count: unread })}</span>}
            </span>
            {unread > 0 && (
              <button
                onClick={handleMarkAll}
                className="text-[11.5px] font-medium text-indigo-600 dark:text-indigo-300 hover:underline flex items-center gap-1"
              >
                <CheckCheck className="w-3.5 h-3.5" /> {t("notifications.mark_all_read")}
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-[12.5px] ux-text-muted">{t("common.loading")}</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="w-8 h-8 mx-auto mb-2 ux-text-muted opacity-50" />
                <p className="text-[12.5px] ux-text-muted">{t("notifications.empty")}</p>
              </div>
            ) : (
              items.map((n) => {
                const Icon = KIND_ICON[n.kind] || Bell;
                return (
                  <button
                    key={n._id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 flex gap-3 border-b ux-border hover:ux-surface-2 transition ${
                      n.readAt ? "" : "bg-indigo-50/40 dark:bg-indigo-500/5"
                    }`}
                  >
                    <span className="mt-0.5 w-8 h-8 shrink-0 rounded-lg bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-medium ux-text truncate">{n.title}</span>
                      {n.message && (
                        <span className="block text-[12px] ux-text-muted line-clamp-2">{n.message}</span>
                      )}
                      <span className="block text-[11px] ux-text-muted mt-0.5">{relativeTime(n.createdAt, t)}</span>
                    </span>
                    {!n.readAt && <span className="mt-1.5 w-2 h-2 rounded-full bg-indigo-500 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>

          <button
            onClick={() => {
              setOpen(false);
              navigate(`/${role}/notifications`);
            }}
            className="px-4 py-2.5 text-[12.5px] font-medium text-indigo-600 dark:text-indigo-300 hover:ux-surface-2 border-t ux-border transition"
          >
            {t("notifications.view_all")}
          </button>
        </div>
      )}
    </div>
  );
}
